import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card, Btn, DataTable } from "@/components/shared";

const MODES = ["IMPS", "NEFT", "RTGS"];
const ROLE_TABS = [["retailer", "Retailers"], ["sr", "Sales Reps"], ["distributor", "Distributors"], ["super", "Super Distributors"]];
const NEW_SLAB = { min: 1001, max: 25000, type: "flat", value: 5, min_charge: 0, dt_share: 0, sd_share: 0 };
const COLS = [["min", "From ₹"], ["max", "To ₹"], ["type", "Type"], ["value", "Charge"], ["min_charge", "Min ₹ (for %)"], ["dt_share", "Distributor % of charge"], ["sd_share", "Super Dist. % of charge"]];

export const chargeFor = (plan, mode, amount) => {
  const s = (plan?.[mode] || []).find((x) => amount >= x.min && amount <= x.max);
  if (!s) return null;
  const c = s.type === "flat" ? s.value : (amount * s.value) / 100;
  return Math.round(Math.max(c, s.min_charge || 0) * 100) / 100;
};

function Tabs({ items, value, onChange, prefix }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
      {items.map(([v, l]) => (
        <button type="button" key={v} onClick={() => onChange(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${value === v ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`${prefix}-${v}`}>{l}</button>
      ))}
    </div>
  );
}

function SlabCell({ k, s, set, testid }) {
  if (k === "type") {
    return (
      <select className="np-input py-1.5 text-xs" value={s.type} onChange={(e) => set(k, e.target.value)} data-testid={testid}>
        <option value="flat">Flat ₹</option><option value="percent">% of amount</option>
      </select>
    );
  }
  return <input type="number" step="0.01" min="0" className="np-input py-1.5 font-mono text-xs" value={s[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid} />;
}

export function PlanEditor({ initial, onSave, prefix, extra }) {
  const [plan, setPlan] = useState(initial);
  const [mode, setMode] = useState("IMPS");
  const [saving, setSaving] = useState(false);
  useEffect(() => setPlan(initial), [initial]);
  const slabs = plan[mode] || [];
  const update = (list) => setPlan({ ...plan, [mode]: list });
  const setCell = (i) => (k, v) => update(slabs.map((s, j) => (j === i ? { ...s, [k]: k === "type" ? v : v === "" ? "" : Number(v) } : s)));
  const add = () => { const last = slabs[slabs.length - 1]; update([...slabs, last ? { ...NEW_SLAB, min: last.max + 1, max: last.max + 25000 } : NEW_SLAB]); };
  const save = async () => { setSaving(true); try { await onSave(plan); } finally { setSaving(false); } };
  return (
    <div data-testid={`${prefix}-editor`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Tabs items={MODES.map((m) => [m, m])} value={mode} onChange={setMode} prefix={`${prefix}-mode`} />
        <div className="flex gap-2">{extra}<Btn variant="outline" onClick={add} data-testid={`${prefix}-add-slab`}><Plus className="h-4 w-4" />Add slab</Btn></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-xs">
          <thead><tr className="text-slate-500">{COLS.map(([, l]) => <th key={l} className="px-2 py-2 font-semibold">{l}</th>)}<th /></tr></thead>
          <tbody>
            {slabs.map((s, i) => (
              <tr key={i} className="border-t border-slate-100">
                {COLS.map(([k]) => <td key={k} className="px-1 py-1.5"><SlabCell k={k} s={s} set={setCell(i)} testid={`${prefix}-${mode}-${i}-${k}`} /></td>)}
                <td><button type="button" onClick={() => update(slabs.filter((_, j) => j !== i))} className="p-2 text-red-600" data-testid={`${prefix}-${mode}-${i}-remove`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!slabs.length && <tr><td colSpan={8} className="py-4 text-center text-red-600">No slab — {mode} transfers will be blocked</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-500">Distributor / Super Distributor share is a % of the charge, credited to the retailer's upline when the transfer succeeds (2% TDS).</div>
      <Btn className="mt-4" onClick={save} loading={saving} data-testid={`${prefix}-save`}>Save charges</Btn>
    </div>
  );
}

function UserChargesDialog({ user, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (user) api.get(`/admin/charges/user/${user.id}`).then(({ data }) => setD(data)); }, [user]);
  const put = async (plan, msg) => {
    try { const { data } = await api.put(`/admin/charges/user/${user.id}`, { plan }); setD(data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="user-charges-dialog">
        <DialogHeader><DialogTitle className="font-display">Move to Bank charges · {user?.name} ({user?.login_id})</DialogTitle></DialogHeader>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : (
          <>
            <div className="text-xs font-semibold" data-testid="user-charges-source">{d.override ? <span className="text-blue-700">Custom charges for this user</span> : <span className="text-slate-500">Using role charges — edit and save to set custom charges</span>}</div>
            <PlanEditor initial={d.override || d.role_plan} prefix="user-chg" onSave={(p) => put(p, "Custom charges saved")}
              extra={d.override && <Btn variant="outline" onClick={() => put(null, "Back to role charges")} data-testid="user-chg-reset">Use role charges</Btn>} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminCharges() {
  const [cfg, setCfg] = useState(null);
  const [role, setRole] = useState("retailer");
  const [urole, setUrole] = useState("retailer");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const loadUsers = () => api.get("/admin/users", { params: { role: urole } }).then(({ data }) => setUsers(data));
  useEffect(() => { api.get("/admin/charges").then(({ data }) => setCfg(data)); }, []);
  useEffect(() => { loadUsers(); }, [urole]); // eslint-disable-line
  const saveRole = async (plan) => {
    try { const { data } = await api.put(`/admin/charges/role/${role}`, { plan }); setCfg({ ...cfg, roles: { ...cfg.roles, [role]: data.plan } }); toast.success("Role charges saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = users.filter((u) => !q || `${u.login_id} ${u.name} ${u.mobile}`.toLowerCase().includes(q.toLowerCase()));
  if (!cfg) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <PageHeader title="Move to Bank Charges" subtitle="Set IMPS / NEFT / RTGS charges by amount slab — flat ₹ or % — for a whole role, then customise for any user." />
      <Card title="Role-wise charges" action={<Tabs items={ROLE_TABS} value={role} onChange={setRole} prefix="chg-role" />}>
        <PlanEditor key={role} initial={cfg.roles[role]} prefix="role-chg" onSave={saveRole} />
      </Card>
      <Card className="mt-6" title="User-wise charges" action={<Tabs items={ROLE_TABS} value={urole} onChange={setUrole} prefix="chg-user-role" />}>
        <input className="np-input mb-3 max-w-sm" placeholder="Search login ID, name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="chg-user-search" />
        <DataTable testid="chg-users-table" rows={shown} columns={[
          { label: "Login ID", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> }, { label: "Name", key: "name" }, { label: "Mobile", key: "mobile" },
          { label: "Charges", render: (r) => (r.payout_plan ? <span className="text-xs font-semibold text-blue-700">Custom</span> : <span className="text-xs text-slate-400">Role charges</span>) },
          { label: "Action", render: (r) => <button className="text-xs font-semibold text-blue-700" onClick={() => setSel(r)} data-testid={`manage-charges-${r.login_id}`}>Set charges</button> },
        ]} />
      </Card>
      <UserChargesDialog user={sel} onClose={() => { setSel(null); loadUsers(); }} />
    </div>
  );
}
